import React, { useContext } from 'react'
import { HelmetComponent } from '../../components/HelmetComponent';
import { PublisherAndUserContext } from '../../contexts/PublisherAndUserContext';
import { Box, HStack, Heading, Text, Button, Flex, Icon, Stack } from "@chakra-ui/react";
import { FaFileAlt, FaGraduationCap, FaBook } from "react-icons/fa";
import { ContentCard } from '../../components/ContentCard';
import { NavLink } from "react-router";
import { useBreadcrumbs } from '../../contexts/BreadcrumbProvider';
import { useQuery } from '@apollo/client';
import { EDSHOPIFY_DRAFTS, DYNOES } from '../../graphql/queries';
import { editDynoUrl } from '../../utils/helper';

export const HomePage = () => {
  const { user, publisher } = useContext(PublisherAndUserContext);
  const { addBreadcrumb } = useBreadcrumbs();
  // Top draft product (last updated)
  const { data: draftsData } = useQuery(EDSHOPIFY_DRAFTS, {
    variables: { publisherId: publisher?.id, limit: 1, offset: 0 },
    skip: !publisher?.id,
    fetchPolicy: 'cache-and-network',
  });
  const topDraft = draftsData?.edshopifyDrafts?.[0];
  // Most recently updated EdDoc for this user
  const { data: recentDynoData } = useQuery(DYNOES, {
    variables: {
      take: 1,
      skip: 0,
      orderBy: { updatedAt: 'desc' },
      where: { owner: { id: { equals: user?.id } } },
    },
    skip: !user?.id,
    fetchPolicy: 'cache-and-network',
  });
  const recentDyno = recentDynoData?.dynoes?.[0];

  return (
    <>
      <HelmetComponent title={`Home - ${user?.firstName} ${user?.lastName}`} />
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: "calc(100vh - 130px)" }} flexDir="column">
        <HStack spacing={6} align="stretch" textAlign="center" mt={10}>
          <ContentCard
            icon={FaFileAlt}
            title="EdDoc"
            description="Create interactive educational documents with rich media, assessments, and dynamic learning elements."
            features={["Interactive content & quizzes", "Multimedia integration", "Supports course and book compilation"]}
            buttonText="Create EdDoc"
            path="/createeddoc"
          />
          <ContentCard
            icon={FaGraduationCap}
            title="Course"
            description="Build structured learning experiences with multiple EdDocs, designed for linear progression and student tracking."
            features={["Module-based learning paths", "Progress tracking and assessments", "Seamlessly interactive with EdDocs"]}
            buttonText="Create Course"
            path="/createcourse/1"
          />
          <ContentCard
            icon={FaBook}
            title="Book"
            description="Craft digital books with chapters powered by EdDocs, offering non-linear exploration and interactive learning."
            features={["Chapter-based navigation", "Rich media and quizzes embedded in EdDocs", "Open-access structure—read any chapter anytime"]}
            buttonText="Create Book"
            path="/createbook/1"
          />
        </HStack>
        {/* Separate section for drafts + last EdDoc */}
        <Box width="100%" maxW="1000px" mt={10}>
          <Heading size="md" mb={2} textAlign="left">Pick up where you left off</Heading>
          <Text color="gray.600" mb={4}>Jump back into your in-progress items.</Text>
          <Flex gap={6} direction={{ base: 'column', md: 'row' }}>
            {/* Last updated draft product */}
            <Box flex="1" bg="gray.50" border="1px solid #e2e8f0" borderRadius="md" p={5}>
              <Stack spacing={2}>
                <Flex align="center" justify="space-between">
                  <Heading size="sm">Latest Draft</Heading>
                  <NavLink to="/mydrafts" onClick={() => addBreadcrumb("My Drafts", "/mydrafts", true)} end>
                    <Button variant="link" colorScheme="blue">My Drafts</Button>
                  </NavLink>
                </Flex>
                {topDraft ? (
                  <>
                    <Text fontWeight="600">{topDraft.productName}</Text>
                    <Text fontSize="sm" color="gray.600">{topDraft.productType} • {new Date(topDraft.updatedAt).toLocaleString()}</Text>
                    <Flex gap={3} mt={2}>
                      <NavLink to={`/content/${topDraft.id}`} onClick={() => addBreadcrumb(topDraft.productName, `/content/${topDraft.id}`, false)}>
                        <Button size="sm" colorScheme="blue" variant="solid">Edit</Button>
                      </NavLink>
                    </Flex>
                  </>
                ) : (
                  <Text fontSize="sm" color="gray.500">No drafts yet.</Text>
                )}
              </Stack>
            </Box>

            {/* Last updated EdDoc */}
            <Box flex="1" bg="gray.50" border="1px solid #e2e8f0" borderRadius="md" p={5}>
              <Stack spacing={2}>
                <Flex align="center" justify="space-between">
                  <Heading size="sm">Recent EdDoc</Heading>
                  <NavLink to="/myeddocs" onClick={() => addBreadcrumb("My EdDocs", "/myeddocs", true)} end>
                    <Button variant="link" colorScheme="blue">My EdDocs</Button>
                  </NavLink>
                </Flex>
                {recentDyno ? (
                  <>
                    <Text fontWeight="600">{recentDyno.dynoTitle}</Text>
                    <Text fontSize="sm" color="gray.600">{recentDyno.dynoType?.name} • {new Date(recentDyno.updatedAt).toLocaleString()}</Text>
                    <Flex gap={3} mt={2}>
                      <Button size="sm" colorScheme="blue" variant="solid" onClick={() => window.open(editDynoUrl(recentDyno.id), '_self')}>Edit</Button>
                      <NavLink to="/myeddocs" onClick={() => addBreadcrumb("My EdDocs", "/myeddocs", true)}>
                        <Button size="sm" variant="outline">View All</Button>
                      </NavLink>
                    </Flex>
                  </>
                ) : (
                  <Text fontSize="sm" color="gray.500">No recent EdDocs.</Text>
                )}
              </Stack>
            </Box>
          </Flex>
        </Box>
      </Box>
    </>
  )
}
